import { Box, CircularProgress, Typography, Link, Tooltip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import type { AutosaveStatus } from '../../hooks/useAutosave';

interface Props {
  status: AutosaveStatus;
  /** Wird bei Status 'error' als Retry-Aktion angeboten. */
  onRetry?: () => void;
  /** Nur Icon (mit Tooltip) — fuer enge Stellen wie Tabellenzeilen. */
  compact?: boolean;
}

/**
 * Kompakte Statusanzeige fuer Autosave-Formulare — ersetzt den Speichern-Button
 * als Vertrauenssignal (Änderungen erkannt / Speichert… / Gespeichert / Fehler).
 */
export function AutosaveIndicator({ status, onRetry, compact = false }: Props) {
  const { t } = useTranslation('common');
  if (status === 'idle') return null;

  const config: Record<Exclude<AutosaveStatus, 'idle'>, { color: string; label: string; icon: React.ReactNode }> = {
    unsaved: {
      color: 'var(--pm-fg-subtle)',
      label: t('autosave.unsaved', 'Änderungen erkannt…'),
      icon: <EditNoteIcon sx={{ fontSize: 16 }} />,
    },
    saving: {
      color: 'var(--pm-fg-muted)',
      label: t('autosave.saving', 'Speichert…'),
      icon: <CircularProgress size={13} thickness={5} />,
    },
    saved: {
      color: 'var(--pm-success)',
      label: t('autosave.saved', 'Gespeichert'),
      icon: <CloudDoneIcon sx={{ fontSize: 16 }} />,
    },
    blocked: {
      color: 'var(--pm-warning)',
      label: t('autosave.blocked', 'Pflichtfelder ausfüllen — noch nicht gespeichert'),
      icon: <EditNoteIcon sx={{ fontSize: 16 }} />,
    },
    error: {
      color: 'var(--pm-error)',
      label: t('autosave.error', 'Nicht gespeichert'),
      icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
    },
  };

  const c = config[status];

  if (compact) {
    return (
      <Tooltip title={c.label}>
        <Box sx={{ display: 'inline-flex', color: c.color }} data-testid="autosave-status" data-status={status}>
          {c.icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', color: c.color }} data-testid="autosave-status" data-status={status}>
      {c.icon}
      <Typography variant="caption">{c.label}</Typography>
      {status === 'error' && onRetry && (
        <Link component="button" type="button" variant="caption" onClick={onRetry} sx={{ ml: 0.5 }}>
          {t('autosave.retry', 'Erneut versuchen')}
        </Link>
      )}
    </Box>
  );
}
